import React, { useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { AccountBalance, Add, CloudUpload } from "@mui/icons-material";
import moment from "moment";
import { useKostentraegerUploads } from "../../../stores/Kostentraeger/kostentraeger.queries";
import {
  useCreateKostentraeger,
  useUploadKostentraeger,
} from "../../../stores/Kostentraeger/kostentraeger.mutations";
import { CreateKostentraegerDto } from "../../../api-client";

const KOSTENTRAEGER_TYPES = ["GKV", "Privat", "BG", "BW"];

const emptyForm: CreateKostentraegerDto = {
  ik: "",
  name: "",
  type: "Privat",
  tarifGroupId: "",
  street: "",
  zip: "",
  city: "",
};

const KostentraegerManager: React.FC = observer(() => {
  const { data: uploads = [], isLoading } = useKostentraegerUploads();
  const uploadMutation = useUploadKostentraeger();
  const createMutation = useCreateKostentraeger();

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateKostentraegerDto>(emptyForm);

  const handleUploadClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = ".ke0,.ke1,.ke2,.ke4";
    input.onchange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      const files = target.files ? Array.from(target.files) : [];
      if (files.length === 0) return;
      await uploadMutation.mutateAsync(files);
    };
    input.click();
  };

  const handleFormChange = (field: keyof CreateKostentraegerDto, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async () => {
    await createMutation.mutateAsync(form);
    setCreateOpen(false);
    setForm(emptyForm);
  };

  const isUploading = uploadMutation.isPending;

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      {/* HEADER */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <AccountBalance sx={{ mr: 1, fontSize: 28 }} />
          <Box>
            <Typography variant="h5">Kostenträger</Typography>
            <Typography variant="body2" color="text.secondary">
              Kostenträgerdateien hochladen und importieren sowie Kostenträger verwalten
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleUploadClick}
            disabled={isUploading}
            startIcon={isUploading ? <CircularProgress size={18} /> : <CloudUpload />}
          >
            {isUploading ? "Importiere..." : "Kostenträgerdateien hochladen"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setCreateOpen(true)}
            startIcon={<Add />}
          >
            Neuer Kostenträger
          </Button>
        </Box>
      </Box>

      {/* UPLOAD HISTORY */}
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Upload-Verlauf
      </Typography>

      {isLoading ? (
        <CircularProgress size={24} />
      ) : uploads.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Noch keine Uploads vorhanden.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Hochgeladen am</TableCell>
                <TableCell align="right">Dateien</TableCell>
                <TableCell>Dateinamen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uploads.map((upload) => (
                <TableRow key={upload.uploadDate}>
                  <TableCell>
                    {moment(upload.uploadDate, "YYYY-MM-DDTHH-mm-ss").isValid()
                      ? moment(upload.uploadDate, "YYYY-MM-DDTHH-mm-ss").format("DD.MM.YYYY HH:mm")
                      : upload.uploadDate}
                  </TableCell>
                  <TableCell align="right">{upload.fileCount}</TableCell>
                  <TableCell
                    sx={{ maxWidth: 480, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                    title={(upload.files || []).join(", ")}
                  >
                    {(upload.files || []).join(", ")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* CREATE DIALOG */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neuer Kostenträger</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="IK (Institutionskennzeichen)"
              value={form.ik}
              onChange={(e) => handleFormChange("ik", e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => handleFormChange("name", e.target.value)}
              required
              fullWidth
            />
            <TextField
              select
              label="Typ"
              value={form.type}
              onChange={(e) => handleFormChange("type", e.target.value)}
              fullWidth
            >
              {KOSTENTRAEGER_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Tarifgruppe (ID, optional)"
              value={form.tarifGroupId}
              onChange={(e) => handleFormChange("tarifGroupId", e.target.value)}
              fullWidth
            />
            {form.type !== "GKV" && (
              <>
                <TextField
                  label="Straße"
                  value={form.street}
                  onChange={(e) => handleFormChange("street", e.target.value)}
                  fullWidth
                />
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="PLZ"
                    value={form.zip}
                    onChange={(e) => handleFormChange("zip", e.target.value)}
                    sx={{ width: 160 }}
                  />
                  <TextField
                    label="Ort"
                    value={form.city}
                    onChange={(e) => handleFormChange("city", e.target.value)}
                    fullWidth
                  />
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!form.ik || !form.name || createMutation.isPending}
          >
            {createMutation.isPending ? "Speichere..." : "Erstellen"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default KostentraegerManager;
